/* eslint-disable react/button-has-type */
import React from 'react';
import PropTypes from 'prop-types';
import Button from '../Button';
import Stack from '@mui/material/Stack';

const defaultButton = {
  name: undefined,
  action: () => {},
  style: 'text',
  disabled: false,
  isBusy: false,
  size: 'large',
  color: 'primary',
};

function ButtonSet({
  primary = {
    ...defaultButton,
    style: 'contained',
    dark: false,
  },
  secondary = { ...defaultButton },
  tertiary = { ...defaultButton },
  orientation = 'horizontal',
}) {
  // stackSpacing places a gap between the buttons, either horizontally or vertically
  const stackSpacing = orientation === 'horizontal' ? 2 : 0.5;

  // The following variables determine how the flex containers will be displayed
  // stackDirection specifies the flex-direction for the group of elements
  // stackJustification specifies the 'flex' justifyContent
  const stackDirection =
    orientation === 'horizontal' ? 'row-reverse' : 'column';
  const stackJustification =
    orientation === 'horizontal' ? 'flex-start' : 'center';

  // primary SX sets the minWidth of the primary button so that it does not take up the
  // full width of its container when oriented as a column
  const primarySX = orientation === 'vertical' ? { minWidth: '50%' } : {};

  // combine default button characteristics with the passed button props
  const pButton = { ...defaultButton, style: 'contained', ...primary };
  const sButton = { ...defaultButton, color: 'neutral', ...secondary };
  const tButton = { ...defaultButton, color: 'secondary', ...tertiary };

  return (
    <Stack
      spacing={stackSpacing}
      direction={stackDirection}
      justifyContent={stackJustification}
      alignItems="center"
    >
      {pButton.name && (
        <Button
          variant={pButton.style}
          disabled={pButton.disabled}
          onClick={pButton.action}
          size={pButton.size}
          color={pButton.color}
          sx={primarySX}
          loading={pButton.isBusy}
        >
          <span>{pButton.name}</span>
        </Button>
      )}
      <Stack spacing={2} direction="row" justifyContent={stackJustification}>
        {sButton.name && (
          <Button
            variant={sButton.style}
            disabled={sButton.disabled}
            onClick={sButton.action}
            size={sButton.size}
            color={sButton.color}
            loading={sButton.isBusy}
          >
            <span>{sButton.name}</span>
          </Button>
        )}
        {tButton.name && (
          <Button
            variant={tButton.style}
            disabled={tButton.disabled}
            onClick={tButton.action}
            size={tButton.size}
            color={tButton.color}
            loading={tButton.isBusy}
          >
            <span>{tButton.name}</span>
          </Button>
        )}
      </Stack>
    </Stack>
  );
}

const buttonProps = {
  name: PropTypes.string,
  action: PropTypes.func,
  style: PropTypes.oneOf(['outlined', 'contained', 'text']),
  disabled: PropTypes.bool,
  isBusy: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  color: PropTypes.oneOf(['primary', 'secondary', 'neutral', 'alert']),
};

ButtonSet.propTypes = {
  primary: PropTypes.shape(buttonProps),
  secondary: PropTypes.shape(buttonProps),
  tertiary: PropTypes.shape(buttonProps),
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
};

export default ButtonSet;
